import * as React from 'react';

import { Checkbox, Col, Loading, SearchField } from '@rematter/ui-web';
import { useAtom, useAtomValue } from 'jotai';

import {
  $cloneDialogSearchTerm,
  $cloneDialogSelectedAccountIds,
  $remoteCloneDialogAccounts,
} from '../../_state/quoteClone.state';
import { useTranslations } from '../../i18n/useTranslations';

import { useDebouncedAtom } from '~/shared/hooks/useDebouncedAtom';

//
// ⚛️ ⚛️ ⚛️ QUOTE CLONE WIZARD STEP CUSTOMERS ⚛️ ⚛️ ⚛️
//
export const QuoteCloneWizardStepCustomers: React.FC<{
  disabled?: boolean;
}> = ({ disabled }) => {
  const t = useTranslations();

  // States
  const [selectedIds, setSelectedIds] = useAtom($cloneDialogSelectedAccountIds);

  // Atoms
  const {
    data: accounts,
    isFetching,
    loadingState,
  } = useAtomValue($remoteCloneDialogAccounts);

  // Other data hooks
  const [searchTerm, setSearchTerm] = useDebouncedAtom(
    $cloneDialogSearchTerm,
    300,
  );

  // Callbacks
  const handleToggle = React.useCallback(
    (accountId: string) => {
      setSelectedIds((prev) =>
        prev.includes(accountId)
          ? prev.filter((id) => id !== accountId)
          : [...prev, accountId],
      );
    },
    [setSelectedIds],
  );

  return (
    <Col gap4 className="@QuoteCloneWizardStepCustomers">
      <SearchField
        value={searchTerm}
        onChange={(value) => setSearchTerm(value ?? '')}
        placeholder={t.quotes.clone.searchPlaceholder}
        aria-label={t.quotes.clone.searchAriaLabel}
        className="w-full"
        isDisabled={disabled}
      />

      <Loading isLoading={loadingState.isInitialLoading}>
        <div className="max-h-[400px] overflow-y-auto">
          {accounts && accounts.length === 0 && !isFetching ? (
            <div className="py-8 text-center text-neutral/60">
              {t.quotes.clone.noAccounts}
            </div>
          ) : (
            <Col>
              {accounts?.map((account) => (
                <div
                  key={account.id}
                  className="border-b border-neutral/10 px-1 py-3 last:border-0"
                >
                  <Checkbox
                    isSelected={selectedIds.includes(account.id)}
                    onChange={() => handleToggle(account.id)}
                    isDisabled={disabled}
                  >
                    <span className="text-sm font-medium">{account.name}</span>
                  </Checkbox>
                </div>
              ))}
            </Col>
          )}
        </div>
      </Loading>
    </Col>
  );
};
